import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ItilTranslationType } from 'api/itilGenius';
import { useApiContext } from 'context/ApiContext';

import type { ItilTranslationStatus } from '../types';
import { ITIL_TEMPLATE_KEY } from '../constants';

type TranslateOptions = {
  ticketId?: number;
  preview?: boolean;
  translationType?: ItilTranslationType;
};

export function useItilTranslation() {
  const { itilGenius } = useApiContext();
  const { t } = useTranslation('global');
  const [status, setStatus] = useState<ItilTranslationStatus>('idle');
  const [translatedText, setTranslatedText] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const translate = useCallback(
    async (rawDescription: string, options: TranslateOptions = {}) => {
      if (!rawDescription?.trim()) {
        setErrorMessage(t('itilGenius.messages.emptyOriginal'));
        return null;
      }

      setStatus('loading');
      setErrorMessage(null);

      const request = {
        rawDescription,
        templateKey: ITIL_TEMPLATE_KEY,
        translationType: options.translationType ?? 'Description'
      } as const;

      try {
        const text =
          options.preview || !options.ticketId
            ? await itilGenius.TranslatePreview(request)
            : await itilGenius.TranslateTicket(options.ticketId, request);

        setTranslatedText(text);
        setStatus('ready');
        return text;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : t('itilGenius.messages.serviceUnavailable');
        setErrorMessage(message);
        setStatus('error');
        return null;
      }
    },
    [itilGenius, t]
  );

  const markStale = useCallback(() => {
    setStatus((prev) => (prev === 'ready' ? 'stale' : prev));
  }, []);

  const continueWithOriginal = useCallback(() => {
    setStatus('storedAsOriginal');
    setErrorMessage(null);
  }, []);

  const skipTranslation = useCallback(() => {
    setStatus('skipped');
    setErrorMessage(null);
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setTranslatedText('');
    setErrorMessage(null);
  }, []);

  const hydrate = useCallback((text: string) => {
    setTranslatedText(text);
    setStatus('ready');
  }, []);

  return {
    status,
    translatedText,
    errorMessage,
    translate,
    markStale,
    continueWithOriginal,
    skipTranslation,
    reset,
    hydrate,
    setStatus
  };
}
